import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../Firebase/firebase-config';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { ref, update, get, remove } from 'firebase/database';
import { getStorage, ref as storageRef, listAll, deleteObject } from 'firebase/storage';
import './DeleteAccount.css';

const DeleteAccount = () => {
    const [confirmed, setConfirmed] = useState(false);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);   // ← Loader State

    const navigate = useNavigate();

    const handleConfirm = () => setConfirmed(true);

    // ==================== STORAGE DELETION ====================
    const deleteFolderRecursive = async (folderRef) => {
        try {
            const listResult = await listAll(folderRef);
            await Promise.all(listResult.items.map(itemRef => deleteObject(itemRef)));

            for (const prefix of listResult.prefixes) {
                await deleteFolderRecursive(prefix);
            }
        } catch (err) {
            console.warn(`Error deleting folder ${folderRef.fullPath}:`, err.message);
        }
    };

    const deleteUserStorage = async (uid) => {
        const storage = getStorage();
        const storageNodes = [
            'Finance', 'POFiles', 'PaymentFiles', 'chatDocuments', 'chatFiles',
            'mtc', 'pendingProducts', 'refunds', 'user-statements', 'userDocuments'
        ];

        for (const node of storageNodes) {
            const folderRef = storageRef(storage, `${node}/${uid}`);
            await deleteFolderRecursive(folderRef);
        }
    };

    // ==================== GET ALL UIDs TO DELETE ====================
    const getUidsToDelete = async (currentUid, accountType) => {
        const uids = new Set([currentUid]);

        try {
            const profileSnap = await get(ref(db, `profileDashboard/${currentUid}`));
            const profileData = profileSnap.exists() ? profileSnap.val() : {};

            if (accountType === 'main') {
                // Firms
                const firmsSnap = await get(ref(db, `profileDashboard/${currentUid}/firms`));
                if (firmsSnap.exists()) {
                    Object.keys(firmsSnap.val()).forEach(firmUid => uids.add(firmUid));
                }

                // Partners
                const allProfilesSnap = await get(ref(db, 'profileDashboard'));
                if (allProfilesSnap.exists()) {
                    Object.entries(allProfilesSnap.val()).forEach(([uid, data]) => {
                        if (data.ownerId === currentUid || data.mainUid === currentUid) {
                            uids.add(uid);
                        }
                    });
                }
            } 
            else if (accountType === 'firm') {
                if (profileData.ownerId || profileData.mainUid) {
                    const mainUid = profileData.ownerId || profileData.mainUid;
                    await remove(ref(db, `profileDashboard/${mainUid}/firms/${currentUid}`));
                }
            }
        } catch (err) {
            console.error("Error preparing deletion list:", err);
        }

        return Array.from(uids);
    };

    // ==================== HANDLE DELETE ====================
    const handleDelete = async () => {
        setIsDeleting(true);
        setError('');

        try {
            const user = auth.currentUser;
            if (!user || !password) {
                alert('Password is required.');
                return;
            }

            // Re-authenticate
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);

            const currentUid = user.uid;

            const profileSnap = await get(ref(db, `profileDashboard/${currentUid}`));
            const profileData = profileSnap.exists() ? profileSnap.val() : {};
            const accountType = profileData.type || 'main';

            const uidsToDelete = await getUidsToDelete(currentUid, accountType);

            console.log(`Deleting account(s):`, uidsToDelete);

            // Database Cleanup
            const nodesToDelete = [
                'issues', 'machineryOrder', 'needHelp', 'Finance', 'negotiations',
                'new-code', 'newProducts', 'notes', 'offers', 'orders', 'priceInformation',
                'profileDashboard', 'queries', 'quoteRequests', 'rates', 'rateRequest',
                'refunds', 'regions', 'steelOrder', 'supplierContacts', 'supplier_notifications',
                'transport_goods', 'transport_goods_replies', 'trendingProducts',
                'userBrandAlternatives', 'userComparisonData', 'user_notifications',
                'userchatbox', 'groupChats', 'ActivePO', 'accessPermissions', 'alt_alarm',
                'broadcasts', 'feedback', 'loanRequest'
            ];

            const updates = {};
            uidsToDelete.forEach(uid => {
                nodesToDelete.forEach(node => {
                    updates[`${node}/${uid}`] = null;
                });
            });

            await update(ref(db), updates);

            // Storage Cleanup
            for (const uid of uidsToDelete) {
                await deleteUserStorage(uid);
            }

            // Delete Auth Account
            await user.delete();

            alert('Account and all related data have been permanently deleted.');
            navigate('/');
            
        } catch (err) {
            console.error("Delete error:", err);
            setError('Error deleting account: ' + (err.message || 'Unknown error'));
        } finally {
            setIsDeleting(false);     // ← Always stop loader
        }
    };

    return (
        <div className="center-wrapper">
            <div className="delete-account-container">
                <h2>Delete Account</h2>

                {!confirmed ? (
                    <>
                        <p>
                            This will permanently delete your account along with all associated data, 
                            firms, partners, and files.
                        </p>
                        <p className="warning-text">⚠️ This action cannot be undone.</p>
                        <div className="button-group">
                            <button 
                                onClick={handleConfirm} 
                                className="delete-button"
                                disabled={isDeleting}
                            >
                                Yes, Delete Account
                            </button>
                            <button 
                                onClick={() => navigate('/profile')} 
                                className="cancel-button"
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <label>Please re-enter your password to confirm:</label>
                        <form onSubmit={(e) => { e.preventDefault(); handleDelete(); }}>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                                disabled={isDeleting}
                            />
                            <button 
                                type="submit" 
                                className="delete-button"
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="dlt-spinner"></span>
                                        Deleting Account...
                                    </>
                                ) : (
                                    "Confirm & Delete"
                                )}
                            </button>
                        </form>
                        <button 
                            onClick={() => navigate('/profile')} 
                            className="cancel-button"
                            disabled={isDeleting}
                        >
                            Cancel
                        </button>
                        {error && <p className="error">{error}</p>}
                    </>
                )}
            </div>
        </div>
    );
};

export default DeleteAccount;